import Parameter from "../../../../../common/components/Parameter";
import { type ProductData } from "../../../../../models/ProductData";
import { type LineItem, PackageEntrySource } from "~/feature/cart/cart/models/CartData";
import { Link } from "../../../../../common/components/Link";
import { getContractDetailsUrl, getQuoteDetailsUrl } from "../../../../../utils/url/urlUtils";
import { HAS_END_CUSTOMER } from "../../../../../utils/constants";
import { userIsDistributor, userIsInquirer, userIsSalesman } from "../../../../../utils/roles";
import { Permission, userHasPermission } from "../../../../../utils/permissions";
import { toMapFn } from "../../../../../utils/reducers";
import { useTranslation } from "react-i18next";

type BaseParamProps = JSX.IntrinsicElements["div"] & {
  product: ProductData;
  lineItem: LineItem;
  "data-test-id"?: string;
  children?: never;
};

export const QuoteItemParam = ({ product, lineItem, ...rest }: BaseParamProps) => {
  const { t } = useTranslation();

  if (lineItem.source !== PackageEntrySource.QUOTE) {
    return null;
  }

  const { quoteLine, quoteNumber } = lineItem;
  const url = getQuoteDetailsUrl(quoteNumber);

  return (
    <Parameter label={t("lineItem.param.quote")} {...rest}>
      <Link href={url}>{quoteNumber}</Link>, {quoteLine}
    </Parameter>
  );
};

export const ContractItemParam = ({ product, lineItem, ...rest }: BaseParamProps) => {
  const { t } = useTranslation();

  if (lineItem.source !== PackageEntrySource.CONTRACT) {
    return null;
  }

  const { sapMaterialNumber } = product;
  const { packCode, contractNumber, contractLine } = lineItem;

  const url = getContractDetailsUrl({ sapMaterialNumber, packCode, contractPosition: contractLine, contractNumber });

  return (
    <Parameter label={t("lineItem.param.contract")} {...rest}>
      <Link href={url}>{contractNumber}</Link>
    </Parameter>
  );
};

interface PriceReferenceParamProps extends Omit<BaseParamProps, "lineItem"> {
  lineItem: Pick<LineItem, "priceReference">;
}
export const PriceReferenceParam = ({ product, lineItem, ...rest }: PriceReferenceParamProps) => {
  const { t } = useTranslation();

  if (!isPriceReferenceParamVisible()) {
    return null;
  }

  // label key cannot be undefined because we checked isPriceReferenceParamVisible() before
  const label = t(getPriceReferenceLabelKey()!);

  return (
    <Parameter label={label} cutOverflow={true} {...rest}>
      {getPriceReferenceLabel(lineItem.priceReference)}
    </Parameter>
  );
};

const priceReferenceLabelMap = toMapFn(
  window.SCHA.config.priceReferences,
  (item) => item.value,
  (item) => item.label,
);
export const getPriceReferenceLabel = (key: string) => (!key ? "-" : priceReferenceLabelMap[key] ?? key);

export const isPriceReferenceParamVisible = () => {
  return userCanSeeEndCustomer() || userCanSeePriceReference();
};

export const userCanSeePriceReference = () =>
  (userIsDistributor() || userIsInquirer() || userIsSalesman()) && window.SCHA.config.priceReferences.length >= 2;
export const userCanSeeEndCustomer = () =>
  userHasPermission(Permission.SEE_ENDCUSTOMER_TABULAR_VIEW) && HAS_END_CUSTOMER;

export const getPriceReferenceLabelKey = () => {
  if (userCanSeeEndCustomer()) {
    return "productDetailPage.endCustomerLabel";
  } else if (userCanSeePriceReference()) {
    return "component.dropdown.label.priceReference";
  }
};
